import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { openLiveConnection } from './liveConnection';

const AuthContext = createContext(null);

// For subscribeRefresh listeners: true if a "refresh" event concerns any of
// `scopes`. An event without a scope (or one from an older server) always
// counts, so a listener never misses an update it would have acted on.
export const isRefreshFor = (event, ...scopes) => !event?.scope || scopes.includes(event.scope);

const TOKEN_KEY = 'ivr_dashboard_token';
const USER_KEY = 'ivr_dashboard_user';

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem(USER_KEY);
    return stored ? JSON.parse(stored) : null;
  });
  const [checkingSession, setCheckingSession] = useState(true);
  const refreshListenersRef = useRef(new Set());
  const incomingCallListenersRef = useRef(new Set());
  const callsListenersRef = useRef(new Set());

  // Merges fields into the logged-in user (e.g. a changed chat wallpaper)
  // and persists them the same way login does, so a reload keeps them.
  const updateUser = useCallback((patch) => {
    setUser((previous) => {
      if (!previous) return previous;
      const next = { ...previous, ...patch };
      localStorage.setItem(USER_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const logout = useCallback(() => {
    // Drops the /uploads files cookie too (best effort: the page logs out
    // either way, and the cookie expires with the session regardless).
    fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
    setToken(null);
    setUser(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }, []);

  // Adds the Authorization header to any request. Any 401 (expired/invalid
  // token) drops the session so the login screen reappears immediately.
  const authFetch = useCallback(
    async (url, options = {}) => {
      const headers = { ...(options.headers || {}) };
      if (token) headers.Authorization = `Bearer ${token}`;
      const response = await fetch(url, { ...options, headers });
      if (response.status === 401) logout();
      return response;
    },
    [token, logout]
  );

  // Revalidates a stored token on load so a stale/tampered token can't be
  // used to skip straight to the dashboard.
  useEffect(() => {
    if (!token) {
      setCheckingSession(false);
      return;
    }
    let cancelled = false;
    fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (!res.ok) throw new Error('Session expired');
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setUser(data.user);
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      })
      .catch(() => {
        if (!cancelled) logout();
      })
      .finally(() => {
        if (!cancelled) setCheckingSession(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, logout]);

  // One shared live-update connection per session — components subscribe to
  // "refresh" instead of each polling on their own timer. Opened with a
  // one-time ticket and reconnected on drop by openLiveConnection
  // (liveConnection.js), which keeps the JWT out of the URL.
  useEffect(() => {
    if (!token) return undefined;

    // Each "refresh" says what changed ({ scope: 'calls' | 'ringing' |
    // 'viewing' | 'whatsapp' | 'email' | 'carts' }), so a listener can skip
    // the ones that aren't about its data — see isRefreshFor below.
    const handleRefresh = (event) => {
      let payload = {};
      try {
        payload = JSON.parse(event.data) || {};
      } catch (error) {
        // No usable scope: listeners treat it as relevant.
      }
      for (const listener of refreshListenersRef.current) listener(payload);
    };

    // IVR -> ERP incoming-call popup module: a named event (distinct from the
    // generic "refresh" signal above) carrying the actual payload to show,
    // so the popup can render instantly without a follow-up fetch.
    const handleIncomingCall = (event) => {
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch (error) {
        console.error('Error parsing ivr:incoming-call payload:', error);
        return;
      }
      for (const listener of incomingCallListenersRef.current) listener(payload);
    };

    // Call-list changes only ({ action: 'upsert' | 'reset', uuid? }) — App.js
    // fetches just the changed calls on this instead of the whole history.
    const notifyCalls = (payload) => {
      for (const listener of callsListenersRef.current) listener(payload);
    };
    const handleCalls = (event) => {
      let payload = {};
      try {
        payload = JSON.parse(event.data) || {};
      } catch (error) {
        // Malformed payload: still worth a catch-up fetch.
      }
      notifyCalls(payload);
    };
    // Fires on connect and on every reconnect. Events sent while the
    // connection was down are lost, so listeners catch up here.
    const handleOpen = () => notifyCalls({ action: 'resync' });

    return openLiveConnection({
      token,
      handlers: {
        refresh: handleRefresh,
        'ivr:incoming-call': handleIncomingCall,
        calls: handleCalls,
        open: handleOpen,
      },
    });
  }, [token]);

  const subscribeRefresh = useCallback((listener) => {
    refreshListenersRef.current.add(listener);
    return () => refreshListenersRef.current.delete(listener);
  }, []);

  const subscribeIncomingCall = useCallback((listener) => {
    incomingCallListenersRef.current.add(listener);
    return () => incomingCallListenersRef.current.delete(listener);
  }, []);

  const subscribeCalls = useCallback((listener) => {
    callsListenersRef.current.add(listener);
    return () => callsListenersRef.current.delete(listener);
  }, []);

  const login = async (username, password) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Login failed');
    setToken(data.token);
    setUser(data.user);
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
  };

  return (
    <AuthContext.Provider
      value={{ user, updateUser, token, login, logout, authFetch, checkingSession, subscribeRefresh, subscribeIncomingCall, subscribeCalls }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
